import React, { useState } from 'react';
import { X, Hash, Lock } from 'lucide-react';
import { useChat } from '../../context/ChatContext';
import { slugify } from '@sahaj/utils';

interface CreateChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultGroupId?: string | undefined;
}

export const CreateChannelModal: React.FC<CreateChannelModalProps> = ({
  isOpen,
  onClose,
  defaultGroupId
}) => {
  const { groups, createChannel } = useChat();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [groupId, setGroupId] = useState(defaultGroupId || (groups[0]?.id || ''));
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (defaultGroupId) {
      setGroupId(defaultGroupId);
    } else if (!groupId && groups.length > 0) {
      const first = groups[0];
      if (first) setGroupId(first.id);
    }
  }, [defaultGroupId, groups, groupId]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !groupId) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await createChannel(groupId, slugify(name.trim()), description.trim() || undefined, isPrivate);
      setName('');
      setDescription('');
      setIsPrivate(false);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create channel');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-base text-foreground">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Hash className="h-4 w-4" />
            </div>
            <span>Create a Channel</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted text-muted-foreground transition">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs font-medium">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Group</label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full h-10 px-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
            >
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Channel Name</label>
            <div className="flex items-center">
              <span className="h-10 px-3 bg-muted border border-r-0 border-border rounded-l-xl text-xs text-muted-foreground flex items-center">
                #
              </span>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(slugify(e.target.value))}
                placeholder="general-announcements"
                className="flex-1 h-10 px-3 bg-background border border-border rounded-r-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition font-mono text-xs"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Description (Optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this channel about?"
              className="w-full h-10 px-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
            />
          </div>

          {/* Private Toggle */}
          <div className="flex items-start gap-3 p-3 bg-muted/40 rounded-xl border border-border/80">
            <input
              type="checkbox"
              id="isPrivate"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <label htmlFor="isPrivate" className="text-xs select-none cursor-pointer">
              <span className="font-semibold text-foreground flex items-center gap-1.5">
                <Lock className="h-3 w-3" /> Make private
              </span>
              <p className="text-muted-foreground mt-0.5">
                When a channel is private, it can only be viewed or joined by invitation.
              </p>
            </label>
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || !groupId || isSubmitting}
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-primary/90 transition shadow-sm disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Channel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
